// Forked from build-a-drip-3d.js on 2026-06-09 for the AM x SD collection.
// Removed: accessory-merge machinery (mergeAccessoryNodes, defaultBaseModel,
// _defaultAccessoryNodes). Added: setMaterialColor(materialName, hex) for
// runtime collar color override.

// === TUNABLES — adjust these to match the BAD configurator's look ===
// Lights (intensities). Defaults were ported from BAD; bumped here because
// the AM x SD model sits at origin instead of BAD's right/down offset, so
// less of its surface area catches the key light at the same angle.
const AMSD_AMBIENT_INTENSITY = 0.35;     // was 0.2 — lifts everything
const AMSD_KEY_INTENSITY = 0.55;         // was 0.4 — main directional
const AMSD_FILL_INTENSITY = 0.3;         // was 0.2 — side fill
const AMSD_TOP_INTENSITY = 0.4;          // was 0.3 — top rim light
// PBR overrides for the collar's enamel material (the one whose color we
// recolor at runtime). Fully matte (roughness 1, no env reflection) so the
// rendered collar reads as close to the Shopify hex chip as possible — the
// forest HDR's green/yellow tint would otherwise dominate the surface even
// at low intensity. The only modulation left is the white directional/
// ambient lighting, which only shades, doesn't shift hue.
const AMSD_COLLAR_ROUGHNESS = 1.0;
const AMSD_COLLAR_ENV_INTENSITY = 0.0;
// PBR overrides for charm materials. We split by material name so the gold
// body reads metallic but the enamel/diamond/stone accents stay matte.
//   - "Charm - Gold" (shared gold body across all charms + collar chain)
//   - everything else on a charm mesh (pink/BLUE/green enamel, *diamond,
//     green/blue sea, B/W Pepper charm 1+2, Lemon/Orange/Marble/Strawberry,
//     DOUBLE ORANGE CHARM)
const AMSD_GOLD_METALNESS = 1.0;
const AMSD_GOLD_ROUGHNESS = 0.15;
const AMSD_GOLD_ENV_INTENSITY = 0.5;
const AMSD_NONGOLD_METALNESS = 0.0;
const AMSD_NONGOLD_ROUGHNESS = 0.5;
// Low env intensity on flat-color materials — forest.exr has a strong
// green/yellow tint that pushed warm baseColors (Orange Round, Double
// Orange, etc.) noticeably toward yellow. 0.2 keeps a hint of env
// shading without rewriting the color.
const AMSD_NONGOLD_ENV_INTENSITY = 0.2;
// Diamond/gem materials (clear / pink / yellow / green / blue Diamond) read
// glassy: high metalness for crisp reflections, near-zero roughness for a
// sharp mirror, strong env-map contribution for the gem sparkle.
const AMSD_DIAMOND_METALNESS = 0.9;
const AMSD_DIAMOND_ROUGHNESS = 0.05;
const AMSD_DIAMOND_ENV_INTENSITY = 1.5;
// Per-charm tint for the bezel center stones. compress.sh's gltf-transform
// optimize dedupes every diamond material in the source GLTF into one shared
// "clear diamond" instance in the compressed GLB, so we can't tint by material
// name (the originals like "blue Diamond" are gone) and we can't tint the
// shared material in place (it would recolor every bezel's stone at once).
// Instead, key by the charm NODE name and clone the diamond submesh's
// material per charm so each one gets its own tint. Keys are exact node names
// from Blender — sanitization (whitespace -> _) is handled at lookup time.
const AMSD_DIAMOND_TINTS_BY_CHARM = {
  "pink blue SD":     "#031B3D", // Pink/Blue Charm (round) — deep blue stone
  "blue pink SD":     "#56092E", // Blue/Pink Charm (oval) — deep magenta stone
  "neon green SD":    "#0C0C01", // Neon/Green Charm (square) — near-black stone
  "green yellow  SD": "#A95308", // Green/Yellow Charm (pear) — orange stone
};
// Polish applied to tinted stones. Goal: keep the base color visible (so
// dielectric, metalness=0) AND add the polished-gem sparkle (clearcoat layer
// on top, very low roughness, full env reflection). The clear-diamond polish
// (metalness=0.9) drowned the tint; pure dielectric (no clearcoat) read as
// flat plastic. Clearcoat sits between the two — base color shows through,
// surface gloss reads as gem.
const AMSD_TINTED_DIAMOND_METALNESS = 0.0;
const AMSD_TINTED_DIAMOND_ROUGHNESS = 0.4; // softer underbody so highlights pop
const AMSD_TINTED_DIAMOND_ENV_INTENSITY = 1.2;
const AMSD_TINTED_DIAMOND_CLEARCOAT = 1.0;
const AMSD_TINTED_DIAMOND_CLEARCOAT_ROUGHNESS = 0.0;
// Matte override for specific painted/textured charms whose baked color map
// reads as plastic under the default non-gold polish. Very high roughness +
// near-zero envMapIntensity kills the highlight so the texture detail comes
// through softly, like a real painted bead.
const AMSD_MATTE_METALNESS = 0.0;
const AMSD_MATTE_ROUGHNESS = 0.95;
const AMSD_MATTE_ENV_INTENSITY = 0.05;
const AMSD_MATTE_MATERIAL_NAMES = new Set([
  "B/W Pepper charm 1",
  "B/W Pepper charm 2",
  "Strawberry Swirl Charm",
]);
// Enamels that should visually match a specific collar hex. The artist puts a
// charm-side enamel ring intended to read as the "same blue/pink/etc." as a
// collar variant, but the GLB stores a different (usually darker)
// baseColorFactor — so the ring renders darker than the collar. Override the
// material's color to the listed hex AND apply the collar polish, so charm
// ring and collar render as one shade. Map: material name -> hex string.
const AMSD_ENAMEL_COLLAR_MATCHES = {
  "BLUE enamel": "#4874A6", // Blue/Pink Charm ring -> Light Blue Collar
};
// Per-charm RENDER offset, in world units. Positive Y moves the rendered
// charm UP (toward the collar); X/Z shift it sideways/in-out. Use when the
// auto-detected hookWorld is off — typically for all-gold charms (PLANE)
// where the bbox top lands at a wing tip rather than the actual hook ring.
// Internally applied as a negation against hookWorld so the resulting
// worldDelta moves the rendered charm in the specified direction.
const AMSD_CHARM_HOOK_OFFSET = {
  "PLANE": [0, 0.05, 0], // raise plane so hook reaches collar
};
// === end TUNABLES ===

// === MULTI-CHARM TUNABLES — Approach A (move-and-reveal) arc positioning ===
// Max charms a customer can stack. Selection beyond this is blocked in the
// configurator. Declared here (loaded first via <script> tag); referenced
// from am-sd-drip.js at call time.
const AMSD_MAX_CHARMS = 5;
// Total angular spread (radians) of the outermost charm pair at N=5. The
// outermost slots sit at ±AMSD_ANGULAR_SPAN/2 from the arc center. The single
// most-likely tuning knob; current 40°.
const AMSD_ANGULAR_SPAN = (60 * Math.PI) / 180;
// Per-N angle offsets, expressed as fractions of AMSD_ANGULAR_SPAN.
// Indexed by (n - 1), so index 0 = N=1, index 4 = N=5. Picks a centered,
// symmetric subset of 5 canonical slot positions at ±½, ±¼, 0. N=4
// intentionally skips center to stay symmetric across 5 fixed positions
// (small "diamond" gap at center).
const AMSD_SLOT_ANGLE_FRACTIONS = [
  [0], // N=1
  [-0.25, 0.25], // N=2
  [-0.25, 0, 0.25], // N=3
  [-0.5, -0.25, 0.25, 0.5], // N=4
  [-0.5, -0.25, 0, 0.25, 0.5], // N=5
];
// When non-null (radians), shifts the cluster's center direction around the
// collar's axis from where the natural charm anchor implies. Use only if the
// artist's charm anchor isn't at the collar's visual front-center.
const AMSD_ARC_CENTER_ANGLE_OVERRIDE = null;
// If true, charms rotate around the collar's axis to remain tangent to the
// arc (face slightly outward). Off by default — flat charms (Big Bezel, Baby
// Bezel) have their own pristine orientation and the tangent rotation twists
// them away from front. Round charms barely show the effect either way.
const AMSD_CHARM_TANGENT_ROTATION = false;
// Rotation axis for the charm cluster, in world space. If null (default),
// auto-detected as the thinnest dimension of the collar's bounding box (a
// flat ring's axis is perpendicular to its plane). Override with an array
// like [0, 0, 1] if the auto-detection picks the wrong axis — check the
// debug console for the detected value.
const AMSD_COLLAR_AXIS_OVERRIDE = null;
// Collar's ring radius (the geometric radius of the circular collar). If null
// (default), auto-detected as half the average of the two large bounding box
// dimensions. Override (in model units) only if auto-detection is off — the
// natural charm anchor is typically far below the ring, so we can't infer
// the radius from the anchor's distance.
const AMSD_COLLAR_RING_RADIUS_OVERRIDE = null;
// Multiplier applied to the auto-detected ring radius. Bounding box gives the
// OUTER edge of the collar tube; charms attach to the tube's inside, so the
// auto-detected value reads slightly too large. Dial 0.80–0.95 until the
// green debug ring sits inside the visible collar tube.
const AMSD_COLLAR_RING_RADIUS_SCALE = 0.95;
// When true, draw debug markers in the 3D scene at model-load time:
//   RED sphere    -> auto-detected collar center
//   YELLOW sphere -> natural charm anchor (artist's placement)
//   GREEN dots    -> auto-detected ring circumference at the detected radius
//   BLUE arrow    -> auto-detected collar axis (from center)
//   CYAN spheres  -> 5 attach points on the ring (where hooks should land)
//   ORANGE spheres -> 5 body positions (attach + dangle, where charm bodies hang)
//   MAGENTA lines -> dangle from attach to body
// Use to verify the geometry the code is computing before tuning visuals.
// Set to false to turn off the markers once tuning is dialed in.
const AMSD_DEBUG_VISUAL = false;
// Name of the mesh in the GLB whose bounding box defines the collar's
// geometry (center, radius, axis). Defaults to "Collar". If the GLB's
// "Collar" mesh isn't the visible ring (e.g., it's a smaller hidden
// element), override here — check the console's mesh-name table to find
// which one is the visible ring.
const AMSD_COLLAR_MESH_NAME = "Collar";
// === end MULTI-CHARM TUNABLES ===

class AmSdDripModelViewer {
  static modelCache = new Map();

  constructor(containerId, modelUrl) {
    this.container = document.getElementById(containerId);

    // Get Shopify asset base URL from data attribute (e.g., "https://cdn.shopify.com/.../assets/")
    // Fallback to S3 if not available
    this.assetBaseUrl =
      this.container?.dataset?.assetBaseUrl ||
      "https://spicydan-build-a-drip.s3.us-east-1.amazonaws.com/";

    if (AMSD_DEBUG_MODE)
      console.log("[AmSdDripModelViewer] Using asset base URL:", this.assetBaseUrl);

    // If modelUrl is just a filename (e.g., "BLACK+ONYX.glb"), prepend asset URL
    // If it's already a full URL, use as-is
    // Note: Filenames are already URL-encoded in metafields
    if (modelUrl && !modelUrl.startsWith("http")) {
      modelUrl = this.assetBaseUrl + modelUrl;
    }

    this.modelUrl = modelUrl || this.assetBaseUrl + "am_sd_collection.glb";
    this.defaultModelUrl = this.modelUrl; // remember original default for fallback

    if (AMSD_DEBUG_MODE)
      console.log("[AmSdDripModelViewer] Default model URL:", this.defaultModelUrl);

    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.model = null;
    this.modelLoaded = false;
    this._currentLoadToken = 0; // used to cancel stale loads
    this._lightingInitialized = false; // ensure lights/env added only once
    this.environmentTexture = null; // cache environment map texture

    this.init();

    // Observe both parent and window resize
    const parent = this.container?.parentElement;
    if (parent) {
      new ResizeObserver(() => this.handleResize()).observe(parent);
    }

    window.addEventListener("resize", () => this.handleResize());
  }

  init() {
    THREE.Cache.enabled = true;
    this.scene = new THREE.Scene();

    const aspect = this.container.clientWidth / this.container.clientHeight;
    this.camera = new THREE.PerspectiveCamera(50, aspect, 0.1, 1000);
    this.camera.position.z = 10;

    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.renderer.setSize(
      this.container.clientWidth,
      this.container.clientHeight
    );
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.outputEncoding = THREE.sRGBEncoding;
    this.renderer.shadowMap.enabled = true;
    this.scene.background = null;
    this.container.appendChild(this.renderer.domElement);

    this.resizeRendererToContainer();
    // Initialize OrbitControls once
    this.controls = new THREE.OrbitControls(
      this.camera,
      this.renderer.domElement
    );
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.enableZoom = true;
    // AM x SD model sits at ~3-4 units from camera (smaller than v12/BAD),
    // so BAD's minDistance=3 left zero zoom-in range. Drop to 1 so the
    // mouse-wheel zoom is usable.
    this.controls.minDistance = 1;
    this.controls.maxDistance = 20;
    this.controls.rotateSpeed = 0.9;
    this.controls.zoomSpeed = 0.7;
    this.controls.panSpeed = 0.25;
    this.loadModel();
    this.animate();
  }

  resizeRendererToContainer() {
    this.resizeContainer();

    const width = this.container.clientWidth;
    const height = this.container.clientHeight;

    this.renderer.setSize(width, height);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  resizeContainer() {
    const containerEl = this.container;
    const parent = containerEl?.parentElement;

    if (!containerEl || !parent) return;

    const parentWidth = parent.clientWidth;
    const parentHeight = parent.clientHeight;

    const aspectRatio = 5 / 6;

    // Start by assuming we use full width
    let width = parentWidth;
    let height = width / aspectRatio;

    // If that height is too tall, base it on height instead
    if (height > parentHeight) {
      height = parentHeight;
      width = height * aspectRatio;
    }

    containerEl.style.width = `${width}px`;
    containerEl.style.height = `${height}px`;
  }

  handleResize() {
    this.resizeRendererToContainer();

    const mobileContainer = document.querySelector(
      ".am-sd-drip-mobile-options"
    );
    const isMobile = window.innerWidth < 1025;

    if (isMobile && this.modelLoaded && mobileContainer) {
      mobileContainer.classList.remove("hidden");
    }
  }

  loadModel() {
    this.toggleLoader(true);

    if (AmSdDripModelViewer.modelCache.has(this.modelUrl)) {
      const cached = AmSdDripModelViewer.modelCache.get(this.modelUrl);

      const clonedScene = cached.scene.clone(true);
      this.setupModel({ ...cached, scene: clonedScene });
      this.toggleLoader(false);
      return;
    }

    const dracoLoader = new THREE.DRACOLoader();
    dracoLoader.setDecoderPath(
      "https://cdn.jsdelivr.net/npm/three@0.137.0/examples/js/libs/draco/"
    );

    const loader = new THREE.GLTFLoader();
    loader.setDRACOLoader(dracoLoader);
    if (typeof MeshoptDecoder !== "undefined") {
      loader.setMeshoptDecoder(MeshoptDecoder);
    }

    const originalLoad = THREE.FileLoader.prototype.load;

    THREE.FileLoader.prototype.load = function (
      url,
      onLoad,
      onProgress,
      onError
    ) {
      this.setRequestHeader({}); // Clear any inherited cache-busting headers
      return originalLoad.call(this, url, onLoad, onProgress, onError);
    };

    loader.load(
      this.modelUrl,
      (gltf) => {
        AmSdDripModelViewer.modelCache.set(this.modelUrl, gltf);

        const clonedScene = gltf.scene.clone(true);
        this.setupModel({ ...gltf, scene: clonedScene });
        this.toggleLoader(false);
      },
      undefined,
      (err) => console.error("Model load error:", err)
    );
  }

  // Public API: load a different model URL dynamically (cached when possible)
  loadModelUrl(newUrl, callback) {
    // If no newUrl provided, revert to default
    let targetUrl = newUrl || this.defaultModelUrl;

    // If targetUrl is just a filename (not a full URL or absolute path), prepend asset base URL
    // Note: Filenames are already URL-encoded in metafields
    if (
      targetUrl &&
      !targetUrl.startsWith("http") &&
      !targetUrl.startsWith("/")
    ) {
      targetUrl = this.assetBaseUrl + targetUrl;
    }

    if (!targetUrl || targetUrl === this.modelUrl) {
      if (callback) callback();
      return;
    }
    this.modelUrl = targetUrl;
    const loadToken = ++this._currentLoadToken;
    this.toggleLoader(true);

    // Dispose previous model (keep shared materials/textures cached by three.js Cache)
    if (this.model) {
      try {
        this.scene.remove(this.model);
      } catch (e) {
        console.warn("Failed removing previous model", e);
      }
      this.model = null;
    }

    const wasDefaultBefore = this.modelUrl === this.defaultModelUrl;
    const finish = (gltf) => {
      if (loadToken !== this._currentLoadToken) return; // stale
      this.setupModel(gltf);
      // Mark swap meta so configurator can decide on camera animation
      this._justSwappedModel = true;
      this._lastSwapWasDefault = wasDefaultBefore;
      this.toggleLoader(false);
      if (callback) callback();
    };

    // Serve from cache if available
    if (AmSdDripModelViewer.modelCache.has(this.modelUrl)) {
      const cached = AmSdDripModelViewer.modelCache.get(this.modelUrl);
      const cloned = cached.scene.clone(true);
      finish({ ...cached, scene: cloned });
      return;
    }

    const dracoLoader = new THREE.DRACOLoader();
    dracoLoader.setDecoderPath(
      "https://cdn.jsdelivr.net/npm/three@0.137.0/examples/js/libs/draco/"
    );
    const loader = new THREE.GLTFLoader();
    loader.setDRACOLoader(dracoLoader);

    loader.load(
      this.modelUrl,
      (gltf) => {
        AmSdDripModelViewer.modelCache.set(this.modelUrl, gltf);
        const cloned = gltf.scene.clone(true);
        finish({ ...gltf, scene: cloned });
      },
      undefined,
      (err) => {
        console.error("Model load error:", err);
        this.toggleLoader(false);
      }
    );
  }

  // Preload (cache only) without swapping current displayed model
  preloadModel(url) {
    if (!url) return;

    // If url is just a filename, prepend asset base URL
    // Note: Filenames are already URL-encoded in metafields
    if (!url.startsWith("http")) {
      url = this.assetBaseUrl + url;
    }

    if (AmSdDripModelViewer.modelCache.has(url)) return;

    const dracoLoader = new THREE.DRACOLoader();
    dracoLoader.setDecoderPath(
      "https://cdn.jsdelivr.net/npm/three@0.137.0/examples/js/libs/draco/"
    );
    const loader = new THREE.GLTFLoader();
    loader.setDRACOLoader(dracoLoader);

    loader.load(
      url,
      (gltf) => {
        if (!AmSdDripModelViewer.modelCache.has(url)) {
          AmSdDripModelViewer.modelCache.set(url, gltf);
          if (AMSD_DEBUG_MODE) console.log("[AmSdDripModelViewer] Preloaded model", url);
        }
      },
      undefined,
      (err) => {
        if (AMSD_DEBUG_MODE)
          console.warn("[AmSdDripModelViewer] Preload failed", url, err);
      }
    );
  }

  // Backwards compatibility alias used in open modal logic
  resize() {
    this.handleResize();
  }

  setupModel(gltf) {
    this.model = gltf.scene;
    this.scene.add(this.model);

    // Initialize lights & environment only once, then just apply env map to new model
    if (!this._lightingInitialized) {
      this.initLightingAndEnvironment();
    } else if (this.environmentTexture || this.scene.environment) {
      const envTex = this.environmentTexture || this.scene.environment;
      this.model.traverse((child) => {
        if (child.isMesh && child.material) {
          child.material.envMap = envTex;
          child.material.needsUpdate = true;
        }
      });
    }

    // Controls already created; ensure constraints intact
    if (this.controls) {
      this.controls.minDistance = 1;
      this.controls.maxDistance = 20;
    }

    const box = new THREE.Box3().setFromObject(this.model);
    const center = box.getCenter(new THREE.Vector3());
    this.model.position.sub(center);
    // Force matrixWorld to propagate to all descendants so subsequent
    // setFromObject() calls on children read the post-move world transform.
    // Without this, Box3.setFromObject uses updateWorldMatrix(false, false)
    // which skips the (stale) parent matrixWorld and bounding boxes come back
    // in the pre-move reference frame.
    this.model.updateMatrixWorld(true);

    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);
    const fov = this.camera.fov * (Math.PI / 180);
    let cameraZ = Math.abs(maxDim / 2 / Math.tan(fov / 2));
    cameraZ *= 1.1;
    this.camera.position.set(0, 0, cameraZ);

    this.camera.lookAt(0, 0, 0);
    // Limit how far user can pan away from origin (after model recentered)
    this._maxPanDistance = maxDim * 0.6; // adjustable scalar

    // Snapshot the initial framing so selectOption can animate back to it.
    if (!this._initialView) {
      this._initialView = {
        position: this.camera.position.clone(),
        target: this.controls ? this.controls.target.clone() : new THREE.Vector3(),
      };
    }

    this.renderer.shadowMap.enabled = true;
    if (this.directionalLight) {
      this.directionalLight.castShadow = true;
    }

    // Hide every charm — direct children of "Empty" other than the Collar.
    // IMPORTANT: do not touch the Empty parent itself or the model root —
    // Three.js inherits visibility, so hiding the parent would also hide
    // the Collar. The Collar's own visible flag defaults to true (left alone).
    const collarNode = this.model.getObjectByName(AMSD_COLLAR_MESH_NAME);
    const emptyNode = this.model.getObjectByName("Empty");
    if (AMSD_DEBUG_MODE) {
      const names = [];
      this.model.traverse((n) => { if (n.name) names.push(n.name); });
      console.log("[AmSdDripModelViewer] all named objects after load:", names);
      console.log(
        `[AmSdDripModelViewer] Collar mesh "${AMSD_COLLAR_MESH_NAME}" found:`,
        !!collarNode,
        "Empty found:",
        !!emptyNode,
      );
      const meshTable = [];
      this.model.traverse((n) => {
        if (n.isMesh && n.name) {
          const box = new THREE.Box3().setFromObject(n);
          const c = box.getCenter(new THREE.Vector3());
          const s = box.getSize(new THREE.Vector3());
          meshTable.push({
            name: n.name,
            center: `${c.x.toFixed(2)}, ${c.y.toFixed(2)}, ${c.z.toFixed(2)}`,
            size: `${s.x.toFixed(2)}, ${s.y.toFixed(2)}, ${s.z.toFixed(2)}`,
          });
        }
      });
      console.log(
        "[AmSdDripModelViewer] mesh bounding boxes (find the one matching the visible ring):",
      );
      console.table(meshTable);
    }
    if (emptyNode) {
      emptyNode.children.forEach((child) => {
        if (child === collarNode) return;
        child.visible = false;
      });
    }

    // Capture pristine local transforms + arc parameters for multi-charm
    // rendering (Approach A: move-and-reveal). We mutate each shown charm's
    // local transform per selection and always re-derive from these snapshots
    // so mutations never compound. See spec § "3D rendering — Approach A".
    this._charmPristine = new Map();
    this._collarCenter = null;
    this._charmAnchor = null;
    this._collarAxis = new THREE.Vector3(0, 1, 0);
    this._collarRingRadius = 0;
    this._naturalDangle = new THREE.Vector3();
    this._naturalRadialDir = new THREE.Vector3();
    this._naturalAttach = new THREE.Vector3();

    if (collarNode && emptyNode) {
      const collarBox = new THREE.Box3().setFromObject(collarNode);
      this._collarCenter = collarBox.getCenter(new THREE.Vector3());
      const collarSize = collarBox.getSize(new THREE.Vector3());

      // Determine the ring's axis (thinnest bounding-box dimension). Override
      // with AMSD_COLLAR_AXIS_OVERRIDE if auto-detection picks the wrong axis.
      if (AMSD_COLLAR_AXIS_OVERRIDE) {
        this._collarAxis = new THREE.Vector3(
          AMSD_COLLAR_AXIS_OVERRIDE[0],
          AMSD_COLLAR_AXIS_OVERRIDE[1],
          AMSD_COLLAR_AXIS_OVERRIDE[2],
        ).normalize();
      } else if (collarSize.x <= collarSize.y && collarSize.x <= collarSize.z) {
        this._collarAxis = new THREE.Vector3(1, 0, 0);
      } else if (collarSize.y <= collarSize.x && collarSize.y <= collarSize.z) {
        this._collarAxis = new THREE.Vector3(0, 1, 0);
      } else {
        this._collarAxis = new THREE.Vector3(0, 0, 1);
      }

      // Estimate the collar's ring radius — average of the two large bounding
      // box dimensions (the diameter directions, perpendicular to the axis).
      const sortedSizes = [collarSize.x, collarSize.y, collarSize.z].sort(
        (a, b) => b - a,
      );
      this._collarRingRadius =
        AMSD_COLLAR_RING_RADIUS_OVERRIDE ??
        ((sortedSizes[0] + sortedSizes[1]) / 4) *
          AMSD_COLLAR_RING_RADIUS_SCALE;

      // First pass: snapshot pristine local transforms and find a reference
      // charm for the natural anchor.
      let firstCharm = null;
      const charmChildren = [];
      emptyNode.children.forEach((child) => {
        // collarNode is a sibling inside emptyNode; skip it so we don't
        // accidentally snapshot or treat the collar as a charm.
        if (child === collarNode || !child.name) return;
        this._charmPristine.set(child.name, {
          position: child.position.clone(),
          quaternion: child.quaternion.clone(),
          scale: child.scale.clone(),
          hookWorld: null, // filled in by the second pass below
        });
        charmChildren.push(child);
        if (!firstCharm) firstCharm = child;
      });

      if (firstCharm) {
        // All charms share one front-center anchor per the spec; use the
        // first one's world position as the reference.
        firstCharm.updateMatrixWorld(true);
        this._charmAnchor = firstCharm.getWorldPosition(new THREE.Vector3());

        // Split the natural offset into a radial component (in the ring's
        // plane) and an axial component (along the collar's axis). The
        // "natural attach point" is on the ring at the natural radial
        // direction. For multi-charm we rotate the attach point around the
        // collar's axis; each charm's hook is then moved to its slot's
        // attach point (per-charm dangle, see second pass below).
        const naturalOffset = new THREE.Vector3().subVectors(
          this._charmAnchor,
          this._collarCenter,
        );
        const axialMagnitude = naturalOffset.dot(this._collarAxis);
        const axialComponent = this._collarAxis
          .clone()
          .multiplyScalar(axialMagnitude);
        const radialOffset = new THREE.Vector3().subVectors(
          naturalOffset,
          axialComponent,
        );
        this._naturalRadialDir = radialOffset.clone().normalize();
        if (AMSD_ARC_CENTER_ANGLE_OVERRIDE !== null) {
          // Shift the natural radial direction by the override angle around
          // the collar axis to move the cluster's center.
          this._naturalRadialDir.applyAxisAngle(
            this._collarAxis,
            AMSD_ARC_CENTER_ANGLE_OVERRIDE,
          );
        }
        this._naturalAttach = new THREE.Vector3()
          .copy(this._collarCenter)
          .add(
            this._naturalRadialDir
              .clone()
              .multiplyScalar(this._collarRingRadius),
          )
          .add(axialComponent);
        this._naturalDangle = new THREE.Vector3().subVectors(
          this._charmAnchor,
          this._naturalAttach,
        );

        // Second pass: each charm has its own mesh geometry, so the hook
        // (the small gold loop at the top) sits at a different world position
        // per charm even though all node origins share the natural anchor.
        // For each charm we look for sub-meshes whose material name contains
        // "gold" — these are always the hook rings in the AM x SD GLB — and
        // take the top-center of their combined bounding box. This works
        // even when the hook is offset to the side (e.g. River Shell's hook
        // sits on the shoulder of the shell, not the top center).
        //
        // Fallback: if no gold sub-mesh is found, use the whole charm's
        // bounding box top-center. At render time, each charm gets a per-
        // charm world delta = slot.attach - pristine.hookWorld so its hook
        // lands on the slot's attach point regardless of charm size or
        // hook placement.
        const upDir = new THREE.Vector3()
          .subVectors(this._naturalAttach, this._charmAnchor)
          .normalize();
        const topCenterInUpDir = (box) => {
          const center = box.getCenter(new THREE.Vector3());
          const halfSize = box.getSize(new THREE.Vector3()).multiplyScalar(0.5);
          const upExtent =
            Math.abs(halfSize.x * upDir.x) +
            Math.abs(halfSize.y * upDir.y) +
            Math.abs(halfSize.z * upDir.z);
          return center.add(upDir.clone().multiplyScalar(upExtent));
        };
        for (const child of charmChildren) {
          const goldBox = new THREE.Box3().makeEmpty();
          child.traverse((sub) => {
            if (!sub.isMesh || !sub.material) return;
            const mats = Array.isArray(sub.material)
              ? sub.material
              : [sub.material];
            const hasGold = mats.some(
              (m) => m && m.name && m.name.toLowerCase().includes("gold"),
            );
            if (hasGold) {
              goldBox.union(new THREE.Box3().setFromObject(sub));
            }
          });

          let hookWorld;
          if (!goldBox.isEmpty()) {
            hookWorld = topCenterInUpDir(goldBox);
          } else {
            const fullBox = new THREE.Box3().setFromObject(child);
            if (fullBox.isEmpty()) continue;
            hookWorld = topCenterInUpDir(fullBox);
          }

          // Per-charm render offset. Negated when applied to hookWorld so
          // the resulting worldDelta moves the rendered charm in the
          // user-facing direction (positive Y = up toward collar).
          const offset = AMSD_CHARM_HOOK_OFFSET[child.name];
          if (offset) {
            hookWorld.add(
              new THREE.Vector3(-offset[0], -offset[1], -offset[2]),
            );
          }

          const pristine = this._charmPristine.get(child.name);
          if (pristine) pristine.hookWorld = hookWorld;
        }
      }

      if (AMSD_DEBUG_MODE) {
        console.log("[AmSdDripModelViewer] multi-charm setup:", {
          collarCenter: this._collarCenter?.toArray(),
          collarSize: collarSize.toArray(),
          collarAxis: this._collarAxis.toArray(),
          collarRingRadius: this._collarRingRadius,
          charmAnchor: this._charmAnchor?.toArray(),
          naturalRadialDir: this._naturalRadialDir.toArray(),
          naturalDangle: this._naturalDangle.toArray(),
          pristineCount: this._charmPristine.size,
        });
      }

      if (AMSD_DEBUG_VISUAL) {
        this._drawDebugVisuals();
      }
    }

    // Polish materials by name + context. The decision lattice:
    //   name starts with "Charm - Gold"   -> AMSD_GOLD_*     (gold body + hook
    //                                                          rings; Blender
    //                                                          duplicates the
    //                                                          material on import
    //                                                          and names them
    //                                                          "Charm - Gold.002"
    //                                                          etc., so match
    //                                                          the prefix)
    //   "collar material change RGB"      -> AMSD_COLLAR_*   (enamel polish)
    //   name contains "diamond"           -> AMSD_DIAMOND_*  (gem stones)
    //   anything else under "Empty"       -> AMSD_NONGOLD_*  (enamel, sea, shell,
    //   (i.e. any charm mesh that isn't                       yin/yang faces —
    //   the collar or one of the above)                       kept matte; low
    //                                                         envMapIntensity
    //                                                         keeps the forest
    //                                                         HDR from washing
    //                                                         out subtle texture
    //                                                         baked into the
    //                                                         base color map)
    const isInsideEmpty = (node) => {
      let p = node;
      while (p) {
        if (p.name === "Empty") return true;
        p = p.parent;
      }
      return false;
    };

    this.model.traverse((child) => {
      if (!child.isMesh || !child.material) return;
      const mats = Array.isArray(child.material) ? child.material : [child.material];

      mats.forEach((m) => {
        if (!m) return;
        const mname = (m.name || "").toLowerCase();
        if (m.name && m.name.startsWith("Charm - Gold")) {
          m.metalness = AMSD_GOLD_METALNESS;
          m.roughness = AMSD_GOLD_ROUGHNESS;
          m.envMapIntensity = AMSD_GOLD_ENV_INTENSITY;
          m.needsUpdate = true;
        } else if (m.name === "collar material change RGB") {
          m.roughness = AMSD_COLLAR_ROUGHNESS;
          m.envMapIntensity = AMSD_COLLAR_ENV_INTENSITY;
          m.needsUpdate = true;
        } else if (mname.includes("diamond")) {
          // Default diamond polish — chrome-glass. Per-charm tints (which
          // need a different polish + their own material clone) are applied
          // in a second pass below.
          m.metalness = AMSD_DIAMOND_METALNESS;
          m.roughness = AMSD_DIAMOND_ROUGHNESS;
          m.envMapIntensity = AMSD_DIAMOND_ENV_INTENSITY;
          m.needsUpdate = true;
        } else if (AMSD_MATTE_MATERIAL_NAMES.has(m.name)) {
          m.metalness = AMSD_MATTE_METALNESS;
          m.roughness = AMSD_MATTE_ROUGHNESS;
          m.envMapIntensity = AMSD_MATTE_ENV_INTENSITY;
          m.needsUpdate = true;
        } else if (AMSD_ENAMEL_COLLAR_MATCHES[m.name] && m.color) {
          const hex = AMSD_ENAMEL_COLLAR_MATCHES[m.name];
          m.color.setHex(parseInt(hex.replace("#", ""), 16));
          m.metalness = 0;
          m.roughness = AMSD_COLLAR_ROUGHNESS;
          m.envMapIntensity = AMSD_COLLAR_ENV_INTENSITY;
          m.needsUpdate = true;
        } else if (isInsideEmpty(child) && child !== collarNode) {
          m.metalness = AMSD_NONGOLD_METALNESS;
          m.roughness = AMSD_NONGOLD_ROUGHNESS;
          m.envMapIntensity = AMSD_NONGOLD_ENV_INTENSITY;
          m.needsUpdate = true;
        }
      });
    });

    // Second pass: per-charm diamond tints. The dedupe pass in compress.sh
    // collapsed every diamond material into one shared instance, so we clone
    // the diamond submesh's material per bezel charm and apply that charm's
    // tint. Without this clone, mutating the shared material would tint
    // every other charm's stone in the same step.
    if (emptyNode) {
      const sanitize = (s) =>
        s.replace(/\s/g, "_").replace(/[\[\].:\/]/g, "");
      const tintBySanitized = {};
      for (const [name, hex] of Object.entries(AMSD_DIAMOND_TINTS_BY_CHARM)) {
        tintBySanitized[sanitize(name)] = hex;
      }
      emptyNode.children.forEach((charm) => {
        if (!charm.name) return;
        const tintHex =
          AMSD_DIAMOND_TINTS_BY_CHARM[charm.name] ||
          tintBySanitized[charm.name];
        if (!tintHex) return;
        const tintInt = parseInt(tintHex.replace("#", ""), 16);
        charm.traverse((sub) => {
          if (!sub.isMesh || !sub.material) return;
          const mats = Array.isArray(sub.material)
            ? sub.material
            : [sub.material];
          const cloned = mats.map((m) => {
            if (!m || !m.name || !m.name.toLowerCase().includes("diamond")) {
              return m;
            }
            // Upgrade to MeshPhysicalMaterial so we can use clearcoat. The
            // GLTFLoader loads diamonds as plain MeshStandardMaterial; the
            // clearcoat property exists on Standard but is only respected
            // when the material is Physical.
            const c = new THREE.MeshPhysicalMaterial({
              name: m.name,
              color: m.color ? m.color.clone() : new THREE.Color(0xffffff),
              metalness: AMSD_TINTED_DIAMOND_METALNESS,
              roughness: AMSD_TINTED_DIAMOND_ROUGHNESS,
              envMap: m.envMap || null,
              envMapIntensity: AMSD_TINTED_DIAMOND_ENV_INTENSITY,
              clearcoat: AMSD_TINTED_DIAMOND_CLEARCOAT,
              clearcoatRoughness: AMSD_TINTED_DIAMOND_CLEARCOAT_ROUGHNESS,
              transparent: m.transparent,
              opacity: m.opacity,
              side: m.side,
            });
            c.color.setHex(tintInt);
            c.needsUpdate = true;
            return c;
          });
          sub.material = Array.isArray(sub.material) ? cloned : cloned[0];
        });
      });
    }

    // Dispatch namespaced event so we don't fire BAD's listeners and vice versa
    window.dispatchEvent(new CustomEvent("am-sd-model-loaded"));
  }

  // One-time lighting & environment setup
  initLightingAndEnvironment() {
    if (this._lightingInitialized) return;

    this.ambientLight = new THREE.AmbientLight(0xffffff, AMSD_AMBIENT_INTENSITY);
    this.directionalLight = new THREE.DirectionalLight(0xffffff, AMSD_KEY_INTENSITY);
    this.directionalLight.position.set(5, 5, 5);
    this.directionalLight.castShadow = true;
    this.directionalLight.shadow.bias = -0.0005;

    this.fillLight = new THREE.DirectionalLight(0xffffff, AMSD_FILL_INTENSITY);
    this.fillLight.position.set(-3, 2, -3);

    this.topLight = new THREE.DirectionalLight(0xffffff, AMSD_TOP_INTENSITY);
    this.topLight.position.set(0, 3, 2);

    this.scene.add(
      this.ambientLight,
      this.directionalLight,
      this.fillLight,
      this.topLight
    );

    // Try EXR environment (only once)
    let exrLoader = null;
    try {
      exrLoader = new THREE.EXRLoader();
    } catch (e) {
      exrLoader = null;
    }

    if (!exrLoader) {
      console.warn(
        "EXRLoader unavailable; using fallback cube environment (once)."
      );
      this.loadFallbackEnvironment();
      this._lightingInitialized = true;
      return;
    }

    exrLoader.load(
      "https://cdn.shopify.com/s/files/1/0767/9272/5688/files/forest.exr?v=1743608020",
      (texture) => {
        texture.mapping = THREE.EquirectangularReflectionMapping;
        // Sharpen reflections — default texture filtering reads as "fuzzy"
        // on glossy/metallic surfaces because mipmaps weren't generated and
        // anisotropic sampling defaulted to 1. PMREMGenerator would be the
        // canonical Three.js path, but this simpler setup avoids the cost.
        texture.generateMipmaps = true;
        texture.minFilter = THREE.LinearMipmapLinearFilter;
        texture.magFilter = THREE.LinearFilter;
        texture.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
        texture.needsUpdate = true;
        this.scene.environment = texture;
        this.environmentTexture = texture;
        if (this.model) {
          this.model.traverse((child) => {
            if (child.isMesh && child.material) {
              child.material.envMap = texture;
              child.material.needsUpdate = true;
            }
          });
        }
      },
      undefined,
      (err) => {
        console.warn("EXR environment load failed; fallback to cube.", err);
        this.loadFallbackEnvironment();
      }
    );

    this._lightingInitialized = true;
  }

  // Compute slot descriptors for N charms along the collar's ring.
  // For each slot we rotate the "attach point" (a point on the ring's
  // circumference, at the collar's actual radius) around the collar's axis,
  // then add the constant "dangle" vector — the offset from the attach point
  // to the charm body. Each charm hangs the same way from its own attach
  // point, like real pendants on a necklace.
  // Returns an array of length N — element i has the world-space target
  // position and the rotation delta to apply to slot i's charm. Pure: reads
  // only the cached load-time data + tunable constants.
  computeSlotTransforms(n) {
    if (n < 1 || n > AMSD_MAX_CHARMS) return [];
    if (!this._collarCenter || !this._charmAnchor) return [];

    const fractions = AMSD_SLOT_ANGLE_FRACTIONS[n - 1];
    const axis = this._collarAxis;
    // Axial component of the natural offset (preserved across all slots so
    // the cluster sits at the same depth along the axis as the original).
    const axialMagnitude = new THREE.Vector3()
      .subVectors(this._charmAnchor, this._collarCenter)
      .dot(axis);
    const axialComponent = axis.clone().multiplyScalar(axialMagnitude);
    const ringRadius = this._collarRingRadius;
    const dangle = this._naturalDangle;
    const naturalRadialDir = this._naturalRadialDir;

    const slots = [];
    for (const f of fractions) {
      const theta = f * AMSD_ANGULAR_SPAN;
      const rotatedRadialDir = naturalRadialDir
        .clone()
        .applyAxisAngle(axis, theta);
      const attach = new THREE.Vector3()
        .copy(this._collarCenter)
        .add(rotatedRadialDir.multiplyScalar(ringRadius))
        .add(axialComponent);
      const worldPos = new THREE.Vector3().addVectors(attach, dangle);
      slots.push({ worldPos, attach, theta });
    }

    return slots;
  }

  // Draw debug markers in the scene to verify the auto-detected geometry.
  // Enabled by AMSD_DEBUG_VISUAL. Idempotent — removes any previous markers
  // before drawing fresh ones.
  _drawDebugVisuals() {
    if (!this._collarCenter || !this._charmAnchor) return;

    const existing = this.scene.getObjectByName("amsd-debug-visuals");
    if (existing) {
      this.scene.remove(existing);
      existing.traverse((c) => {
        if (c.geometry) c.geometry.dispose();
        if (c.material) c.material.dispose();
      });
    }

    const group = new THREE.Group();
    group.name = "amsd-debug-visuals";
    const r = this._collarRingRadius;

    // RED: collar center
    group.add(
      this._makeDebugSphere(this._collarCenter, r * 0.03, 0xff0000),
    );

    // YELLOW: natural charm anchor
    group.add(
      this._makeDebugSphere(this._charmAnchor, r * 0.025, 0xffff00),
    );

    // GREEN dots: ring circumference at auto-detected radius
    const axis = this._collarAxis;
    const seedUp =
      Math.abs(axis.y) < 0.9
        ? new THREE.Vector3(0, 1, 0)
        : new THREE.Vector3(1, 0, 0);
    const u = new THREE.Vector3().crossVectors(axis, seedUp).normalize();
    const v = new THREE.Vector3().crossVectors(axis, u).normalize();
    const segments = 48;
    for (let i = 0; i < segments; i++) {
      const a = (i / segments) * Math.PI * 2;
      const p = new THREE.Vector3()
        .copy(this._collarCenter)
        .addScaledVector(u, Math.cos(a) * r)
        .addScaledVector(v, Math.sin(a) * r);
      group.add(this._makeDebugSphere(p, r * 0.012, 0x00ff00));
    }

    // BLUE arrow: collar axis from center, length = 1.5 * ringRadius
    group.add(
      new THREE.ArrowHelper(
        axis.clone().normalize(),
        this._collarCenter,
        r * 1.5,
        0x0000ff,
        r * 0.18,
        r * 0.1,
      ),
    );

    // Slot visualization for N=5:
    //   CYAN spheres on the collar = attach points (where each charm's hook
    //     should land — these are what should be ON the visible ring)
    //   ORANGE spheres below the collar = body positions (where each charm's
    //     body will hang, offset from its attach point by the constant dangle)
    //   MAGENTA lines = the dangle from attach to body
    const slots = this.computeSlotTransforms(5);
    const axialMagnitude = new THREE.Vector3()
      .subVectors(this._charmAnchor, this._collarCenter)
      .dot(axis);
    slots.forEach((slot) => {
      // Recompute the attach point for this slot's angle.
      const rotatedRadialDir = this._naturalRadialDir
        .clone()
        .applyAxisAngle(axis, slot.theta);
      const attach = new THREE.Vector3()
        .copy(this._collarCenter)
        .add(rotatedRadialDir.multiplyScalar(r))
        .add(axis.clone().multiplyScalar(axialMagnitude));

      // CYAN: attach point on the collar (should be ON the visible ring)
      group.add(this._makeDebugSphere(attach, r * 0.03, 0x00ffff));
      // ORANGE: body position (slot.worldPos = where the charm body hangs)
      group.add(this._makeDebugSphere(slot.worldPos, r * 0.02, 0xff8800));
      // MAGENTA: dangle line connecting them
      const lineGeom = new THREE.BufferGeometry().setFromPoints([
        attach,
        slot.worldPos,
      ]);
      group.add(
        new THREE.Line(
          lineGeom,
          new THREE.LineBasicMaterial({ color: 0xff00ff }),
        ),
      );
    });

    this.scene.add(group);
  }

  _makeDebugSphere(position, radius, color) {
    const mesh = new THREE.Mesh(
      new THREE.SphereGeometry(radius, 12, 12),
      new THREE.MeshBasicMaterial({ color }),
    );
    mesh.position.copy(position);
    return mesh;
  }

  // Recolor every material instance with the given name. Note: in Three.js a
  // material is shared across every mesh that references it in the GLTF, so
  // setting the color mutates it globally. This is safe for "collar material
  // change RGB" (only the Collar mesh uses it). It would be UNSAFE to call
  // this for "Charm - Gold" because every charm shares that gold material.
  setMaterialColor(materialName, hex) {
    if (!this.model) return;
    const colorInt = parseInt(String(hex).replace("#", ""), 16);
    if (Number.isNaN(colorInt)) {
      if (AMSD_DEBUG_MODE)
        console.warn("[AmSdDripModelViewer] Invalid hex:", hex);
      return;
    }
    let hitCount = 0;
    this.model.traverse((node) => {
      if (!node.isMesh) return;
      const mats = Array.isArray(node.material) ? node.material : [node.material];
      mats.forEach((mat) => {
        if (mat && mat.name === materialName && mat.color) {
          mat.color.setHex(colorInt);
          mat.needsUpdate = true;
          hitCount++;
        }
      });
    });
    if (AMSD_DEBUG_MODE)
      console.log(
        `[AmSdDripModelViewer] setMaterialColor(${materialName}, ${hex}) hit ${hitCount} material instance(s)`
      );
  }

  // Animate camera position + target back to the front-facing framing
  // captured in setupModel. Called by the configurator after every
  // selectOption so the new collar/charm is presented head-on.
  resetCameraView(duration = 600) {
    if (!this._initialView || !this.controls) return;
    const target = this._initialView;
    const startPos = this.camera.position.clone();
    const startTgt = this.controls.target.clone();
    const startTime = performance.now();

    const step = (now) => {
      const t = Math.min((now - startTime) / duration, 1);
      const ease = t * (2 - t); // ease-out
      this.camera.position.lerpVectors(startPos, target.position, ease);
      this.controls.target.lerpVectors(startTgt, target.target, ease);
      this.controls.update();
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  loadFallbackEnvironment() {
    // Fallback to the original cube map if EXRLoader isn't available
    const envMapLoader = new THREE.CubeTextureLoader();
    const envMap = envMapLoader.load([
      "https://threejs.org/examples/textures/cube/Bridge2/posx.jpg",
      "https://threejs.org/examples/textures/cube/Bridge2/negx.jpg",
      "https://threejs.org/examples/textures/cube/Bridge2/posy.jpg",
      "https://threejs.org/examples/textures/cube/Bridge2/negy.jpg",
      "https://threejs.org/examples/textures/cube/Bridge2/posz.jpg",
      "https://threejs.org/examples/textures/cube/Bridge2/negz.jpg",
    ]);

    this.scene.environment = envMap;

    this.model.traverse((child) => {
      if (child.isMesh && child.material) {
        child.material.envMap = envMap;
        child.material.needsUpdate = true;
      }
    });
  }

  animate() {
    requestAnimationFrame(() => this.animate());
    // Clamp pan target to keep model framed
    if (this.controls && this._maxPanDistance) {
      const t = this.controls.target;
      const dist = Math.sqrt(t.x * t.x + t.y * t.y + t.z * t.z);
      if (dist > this._maxPanDistance) {
        const scale = this._maxPanDistance / dist;
        t.set(t.x * scale, t.y * scale, t.z * scale);
        // Also gently pull camera position by same delta to avoid sudden jump
        const cam = this.camera.position;
        cam.set(cam.x * scale, cam.y * scale, cam.z * scale);
      }
    }
    this.renderer.render(this.scene, this.camera);
    this.controls?.update();
  }

  toggleLoader(show) {
    this.modelLoaded = !show;
    const loader = document.getElementById("model-loader");
    const viewerContainer = document.querySelector(".am-sd-drip-container");
    const mobileContainer = document.querySelector(
      ".am-sd-drip-mobile-options"
    );
    const isMobile = window.innerWidth < 768;

    if (loader && viewerContainer) {
      if (show) {
        loader.classList.remove("hidden");
        viewerContainer.classList.remove("visible");
        viewerContainer.classList.add("hidden");

        if (isMobile && mobileContainer) {
          mobileContainer.classList.add("hidden");
        }
      } else {
        loader.classList.add("hidden");
        viewerContainer.classList.remove("hidden");
        viewerContainer.classList.add("visible");

        if (isMobile && mobileContainer) {
          mobileContainer.classList.remove("hidden");
        }
      }
    }
  }
}
